import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

// Types
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface MatchesResponse {
  data: {
    matches: Match[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'BUYER' | 'SELLER';
  businessName?: string;
  acceptTerms?: boolean;
}

export interface Auction {
  id: string;
  sellerId: string;
  matchId: string;
  inventoryId?: string;
  ticketCount: number;
  seatSection: string;
  seatDetails?: string;
  description?: string;
  startingBid: number;
  currentBid: number;
  minIncrement: number;
  startsAt: string;
  endsAt: string;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'SOLD' | 'CANCELLED';
  winningBidId?: string;
  winnerId?: string;
  paymentDueAt?: string;
  match: Match;
  bids?: Bid[];
  seller?: {
    businessName?: string;
    user?: { firstName: string; lastName: string };
  };
}

export interface Bid {
  id: string;
  auctionId: string;
  userId: string;
  amount: number;
  status: 'ACTIVE' | 'OUTBID' | 'WON' | 'CANCELLED';
  createdAt: string;
  user?: { id: string; firstName: string; lastName: string };
  auction?: Auction;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'BUYER' | 'SELLER' | 'ADMIN';
  isActive: boolean;
  createdAt: string;
  seller?: {
    id: string;
    businessName: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    commission: number;
  };
}

export interface Match {
  id: string;
  homeTeam: string;
  awayTeam: string;
  matchDate: string; // Changed from 'date' to 'matchDate' to match schema
  stadium: string;   // Changed from 'venue' to 'stadium' to match schema
  league: string;
  season: string;
  minPricePerTicket?: number; // Nuevo campo para precio mínimo por entrada
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export interface TicketRequest {
  id: string;
  userId: string;
  matchId: string;
  ticketCount: number;  // Changed from 'quantity' to 'ticketCount' to match schema
  totalBudget: number;  // Changed from 'maxBudget' to 'totalBudget' to match schema
  seatSection?: string; // Nuevo campo para sector deseado
  description?: string;
  status: 'ACTIVE' | 'EXPIRED' | 'COMPLETED' | 'CANCELLED';
  expiresAt: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
  };
  match: Match;
  offers?: Offer[];
}

export interface Offer {
  id: string;
  requestId: string;
  sellerId: string;
  quantity: number;
  pricePerTicket: number;
  totalPrice: number;
  seatSection?: string;
  description?: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';
  expiresAt: string;
  createdAt: string;
  seller: {
    businessName: string;
    commission: number;
    isPremium?: boolean;
  };
  request: TicketRequest;
}

export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  isRead: boolean;
  createdAt: string;
}

// Nuevo tipo para Inventario de Vendedores
export interface SellerInventory {
  id: string;
  sellerId: string;
  matchId: string;
  ticketCount: number;
  pricePerTicket: number;
  seatSection: string;
  seatDetails?: string;
  description?: string;
  isAvailable: boolean;
  createdAt: string;
  match: Match;
  seller: {
    businessName: string;
    isPremium: boolean;
  };
}

// Create axios instance
const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Track whether a refresh is in flight to avoid parallel refreshes
  let isRefreshing = false;
  let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (token) prom.resolve(token);
      else prom.reject(error);
    });
    failedQueue = [];
  };

  // Response interceptor — try refresh before redirect on 401
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // If 401 and we have a refresh token, attempt to refresh
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        localStorage.getItem('refreshToken') &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/login')
      ) {
        if (isRefreshing) {
          // Another refresh in flight — queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(instance(originalRequest));
              },
              reject: (err: any) => reject(err),
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = localStorage.getItem('refreshToken');
          const { data } = await axios.post(
            `${instance.defaults.baseURL}/auth/refresh`,
            { refreshToken }
          );
          const newAccessToken = data.data.accessToken;
          const newRefreshToken = data.data.refreshToken;

          localStorage.setItem('token', newAccessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          processQueue(null, newAccessToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Refresh failed — clear tokens and redirect
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Non-401 error handling
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      } else if (error.response?.status === 403) {
        toast.error('No tienes permisos para realizar esta acción.');
      } else if (error.response?.status === 404) {
        toast.error('Recurso no encontrado.');
      } else if (error.response?.status && error.response.status >= 500) {
        toast.error('Error del servidor. Por favor, intenta más tarde.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Tiempo de espera agotado. Verifica tu conexión.');
      } else if (!error.response) {
        toast.error('Error de conexión. Verifica tu conexión a internet.');
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Create API instance
const api = createApiInstance();

// Auth API
export const authAPI = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<ApiResponse<{ user: User; token: string; accessToken?: string; refreshToken?: string; requires2FA?: boolean; tempToken?: string }>>> =>
    api.post('/auth/login', credentials),

  register: (data: RegisterData): Promise<AxiosResponse<ApiResponse<{ user: User; token: string; accessToken?: string; refreshToken?: string }>>> =>
    api.post('/auth/register', data),

  logout: (): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/auth/logout'),

  getProfile: (): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.get('/users/profile'),

  updateProfile: (data: Partial<User> & { currentPassword?: string }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.put('/users/profile', data),

  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.put('/auth/change-password', data),

  forgotPassword: (email: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (data: { token: string; password: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/auth/reset-password', data),
};

// Matches API
export const matchesAPI = {
  getMatches: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    league?: string;
    date?: string;
    fromDate?: string;
    includePast?: boolean;
  }): Promise<AxiosResponse<MatchesResponse>> =>
    api.get('/matches', { params }),

  getMatch: (id: string): Promise<AxiosResponse<ApiResponse<{ match: Match }>>> =>
    api.get(`/matches/${id}`),

  getMatchStats: (id: string): Promise<AxiosResponse<ApiResponse<{
    activeRequests: number;
    activeOffers: number;
    averagePrice: number;
    activeSellers: number;
  }>>> =>
    api.get(`/matches/${id}/stats`),

  createMatch: (data: Omit<Match, 'id' | 'createdAt'>): Promise<AxiosResponse<ApiResponse<{ match: Match }>>> =>
    api.post('/matches', data),

  updateMatch: (id: string, data: Partial<Match>): Promise<AxiosResponse<ApiResponse<{ match: Match }>>> =>
    api.put(`/matches/${id}`, data),

  deleteMatch: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/matches/${id}`),

  getPublicStats: (): Promise<AxiosResponse<ApiResponse<{
    totalMatches: number;
    activeRequests: number;
    totalOffers: number;
    verifiedSellers?: number;
    generatedAt: string;
  }>>> =>
    api.get('/matches/public-stats'),
};

// Requests API
export const requestsAPI = {
  getRequests: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    matchId?: string;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<TicketRequest>>>> =>
    api.get('/requests', { params }),

  getRequest: (id: string): Promise<AxiosResponse<ApiResponse<{ request: TicketRequest }>>> =>
    api.get(`/requests/${id}`),

  getById: (id: string): Promise<AxiosResponse<ApiResponse<{ request: TicketRequest }>>> =>
    api.get(`/requests/${id}`),

  createRequest: (data: {
    matchId: string;
    ticketCount: number;
    totalBudget: number;
    seatSection?: string;
    description?: string;
  }): Promise<AxiosResponse<ApiResponse<{ request: TicketRequest }>>> =>
    api.post('/requests', data),

  updateRequest: (id: string, data: Partial<TicketRequest>): Promise<AxiosResponse<ApiResponse<{ request: TicketRequest }>>> =>
    api.put(`/requests/${id}`, data),

  cancel: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/requests/${id}/cancel`),

  cancelRequest: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/requests/${id}/cancel`),

  deleteRequest: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/requests/${id}`),

  getPublicRequests: (params?: { limit?: number }): Promise<AxiosResponse<ApiResponse<{ requests: TicketRequest[] }>>> =>
    api.get('/requests/public', { params }),
};

export const auctionsAPI = {
  getAuctions: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    matchId?: string;
    search?: string;
  }): Promise<AxiosResponse<ApiResponse<{
    auctions: Auction[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>>> =>
    api.get('/auctions', { params }),

  getAuction: (id: string): Promise<AxiosResponse<ApiResponse<{ auction: Auction }>>> =>
    api.get(`/auctions/${id}`),

  placeBid: (id: string, amount: number): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/auctions/${id}/bid`, { amount }),

  createFromInventory: (data: {
    inventoryId: string;
    startingBid: number;
    minIncrement?: number;
    durationHours?: number;
    description?: string;
  }): Promise<AxiosResponse<ApiResponse<{ auction: Auction }>>> =>
    api.post('/auctions', data),

  getMyBids: (): Promise<AxiosResponse<ApiResponse<{ bids: Bid[] }>>> =>
    api.get('/auctions/my-bids'),
};

// Offers API
export const offersAPI = {
  getPublicOffers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    league?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<Offer>>>> =>
    api.get('/offers/public', { params }),

  getOffers: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    requestId?: string;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<Offer>>>> =>
    api.get('/offers', { params }),

  getOffer: (id: string): Promise<AxiosResponse<ApiResponse<{ offer: Offer }>>> =>
    api.get(`/offers/${id}`),

  createOffer: (data: {
    requestId: string;
    quantity: number;
    pricePerTicket: number;
    description?: string;
  }): Promise<AxiosResponse<ApiResponse<{ offer: Offer }>>> =>
    api.post('/offers', data),

  updateOffer: (id: string, data: Partial<Offer>): Promise<AxiosResponse<ApiResponse<{ offer: Offer }>>> =>
    api.put(`/offers/${id}`, data),

  acceptOffer: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/offers/${id}/accept`),

  rejectOffer: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/offers/${id}/reject`),

  deleteOffer: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/offers/${id}`),

  bulkUpdatePrice: (data: { offerIds: string[]; adjustment: 'fixed' | 'percentage'; value: number }) =>
    api.put('/offers/bulk/price', data),

  bulkWithdraw: (offerIds: string[]) =>
    api.put('/offers/bulk/withdraw', { offerIds }),
};

// Notifications API
export const notificationAPI = {
  getNotifications: (params?: {
    page?: number;
    limit?: number;
    isRead?: boolean;
  }): Promise<AxiosResponse<ApiResponse<{ notifications: Notification[] }>>> =>
    api.get('/notifications', { params }),

  markAsRead: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/notifications/${id}/read`),

  markAllAsRead: (): Promise<AxiosResponse<ApiResponse>> =>
    api.put('/notifications/mark-all-read'),

  deleteNotification: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/notifications/${id}`),

  getUnreadCount: (): Promise<AxiosResponse<ApiResponse<{ count: number }>>> =>
    api.get('/notifications/unread-count'),
};

// Sellers API
export const sellersAPI = {
  // Apply to be a seller
  applyToBeSeller: (data: {
    businessName?: string;
    taxId?: string;
    address?: string;
  }): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/sellers/apply', data),

  // Get seller profile
  getProfile: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/sellers/profile'),

  // Update seller profile
  updateProfile: (data: {
    businessName?: string;
    taxId?: string;
    address?: string;
  }): Promise<AxiosResponse<ApiResponse>> =>
    api.put('/sellers/profile', data),

  // Get seller offers
  getOffers: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<Offer>>>> =>
    api.get('/sellers/offers', { params }),

  // Get all active requests for sellers
  getRequests: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<TicketRequest>>>> =>
    api.get('/sellers/requests', { params }),

  // Get seller statistics
  getStats: (): Promise<AxiosResponse<ApiResponse<{
    offers: {
      total: number;
      pending: number;
      accepted: number;
      rejected: number;
      thisMonth: number;
    };
    revenue: {
      total: number;
      thisMonth: number;
    };
    performance: {
      acceptanceRate: number;
      averageOfferValue: number;
    };
  }>>> =>
    api.get('/sellers/stats'),

  // Get seller public reputation
  getPublicProfile: (sellerId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/sellers/${sellerId}/reputation`),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: (params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<User>>>> =>
    api.get('/admin/users', { params }),

  updateUser: (id: string, data: Partial<User>): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.put(`/admin/users/${id}`, data),

  deleteUser: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/admin/users/${id}`),

  // Sellers
  getSellers: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<any>>>> =>
    api.get('/admin/sellers', { params }),

  approveSeller: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/admin/sellers/${id}/approve`),

  rejectSeller: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/admin/sellers/${id}/reject`),

  updateSellerCommission: (id: string, commission: number): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/admin/sellers/${id}/commission`, { commission }),

  // Statistics
  getStats: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/admin/stats'),
};

// Users API
export const usersAPI = {
  // Get user dashboard statistics
  getDashboardStats: (): Promise<AxiosResponse<ApiResponse<{
    stats: {
      activeRequests: number;
      pendingOffers: number;
      completedTransactions: number;
      totalSpent: number;
    }
  }>>> =>
    api.get('/users/dashboard-stats'),

  // Get user recent activity
  getRecentActivity: (params?: {
    limit?: number;
  }): Promise<AxiosResponse<ApiResponse<{
    activity: Array<{
      id: string;
      type: 'request_created' | 'offer_received' | 'transaction_completed';
      title: string;
      description: string;
      date: string;
      status: 'success' | 'warning' | 'info' | 'error';
    }>
  }>>> =>
    api.get('/users/recent-activity', { params }),

  // Get user profile
  getProfile: (): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.get('/users/profile'),

  // Update user profile
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> =>
    api.put('/users/profile', data),

  // Change password
  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<AxiosResponse<ApiResponse>> =>
    api.put('/users/change-password', data),
};

// Inventory API (nuevo)
export const inventoryAPI = {
  // Get seller's own inventory
  getInventory: (params?: {
    page?: number;
    limit?: number;
    matchId?: string;
    isAvailable?: boolean;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<SellerInventory>>>> =>
    api.get('/inventory', { params }),

  // Get public inventory (for buyers)
  getPublicInventory: (params?: {
    page?: number;
    limit?: number;
    matchId?: string;
    league?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<AxiosResponse<ApiResponse<PaginatedResponse<SellerInventory>>>> =>
    api.get('/inventory/public', { params }),

  // Get single inventory item
  getInventoryItem: (id: string): Promise<AxiosResponse<ApiResponse<{ inventory: SellerInventory }>>> =>
    api.get(`/inventory/${id}`),

  // Create inventory item
  createInventory: (data: {
    matchId: string;
    ticketCount: number;
    pricePerTicket: number;
    seatSection: string;
    seatDetails?: string;
    description?: string;
  }): Promise<AxiosResponse<ApiResponse<{ inventory: SellerInventory }>>> =>
    api.post('/inventory', data),

  // Update inventory item
  updateInventory: (id: string, data: {
    ticketCount?: number;
    pricePerTicket?: number;
    seatSection?: string;
    seatDetails?: string;
    description?: string;
    isAvailable?: boolean;
  }): Promise<AxiosResponse<ApiResponse<{ inventory: SellerInventory }>>> =>
    api.put(`/inventory/${id}`, data),

  // Delete inventory item
  deleteInventory: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/inventory/${id}`),

  bulkUpdatePrice: (data: { ids: string[]; adjustment: 'fixed' | 'percentage'; value: number }) =>
    api.put('/inventory/bulk/price', data),

  bulkDelete: (ids: string[]) =>
    api.delete('/inventory/bulk', { data: { ids } }),
};

// Payments API
export const paymentsAPI = {
  // Create PaymentIntent for offer
  createIntent: (offerId: string): Promise<AxiosResponse<ApiResponse<{
    clientSecret: string;
    paymentId: string;
    amount: number;
    commission: number;
    sellerPayout: number;
  }>>> =>
    api.post('/payments/create-intent', { offerId }),

  createAuctionIntent: (auctionId: string, acceptTerms?: boolean): Promise<AxiosResponse<ApiResponse<{
    clientSecret: string;
    paymentId: string;
    amount: number;
    commission: number;
    sellerPayout: number;
    auction?: Auction;
  }>>> =>
    api.post('/payments/create-intent-auction', { auctionId, acceptTerms }),

  // Get payment details
  getPayment: (id: string): Promise<AxiosResponse<ApiResponse<{
    payment: {
      id: string;
      offerId?: string | null;
      auctionId?: string | null;
      amount: number;
      commission: number;
      sellerPayout: number;
      status: string;
      escrowStartedAt: string | null;
      escrowExpiresAt: string | null;
      sellerConfirmedAt: string | null;
      buyerConfirmedAt: string | null;
      offer?: Offer;
      auction?: Auction;
    };
  }>>> =>
    api.get(`/payments/${id}`),

  // Get payment by offer ID
  getPaymentByOffer: (offerId: string): Promise<AxiosResponse<ApiResponse<{
    payment: {
      id: string;
      status: string;
      escrowStartedAt: string | null;
      escrowExpiresAt: string | null;
      sellerConfirmedAt: string | null;
      buyerConfirmedAt: string | null;
    };
  }>>> =>
    api.get(`/payments/offer/${offerId}`),

  // Confirm payment (webhook called server-side, but we poll status)
  confirmPaymentStatus: (id: string): Promise<AxiosResponse<ApiResponse<{
    status: string;
  }>>> =>
    api.get(`/payments/${id}/status`),

  // Buyer confirms receipt
  buyerConfirm: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/payments/${id}/buyer-confirm`),

  // Seller confirms delivery
  sellerConfirm: (id: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/payments/${id}/seller-confirm`),

  // Open dispute
  openDispute: (id: string, reason: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/payments/${id}/dispute`, { reason }),

  // Get my payments (buyer or seller)
  getMyPayments: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    role?: 'buyer' | 'seller';
  }): Promise<AxiosResponse<ApiResponse<{
    payments: any[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }>>> =>
    api.get('/payments/my/list', { params }),
};

// Stripe Account API (for sellers)
export const stripeAccountAPI = {
  // Start Stripe Connect onboarding
  connect: (): Promise<AxiosResponse<ApiResponse<{
    onboardingUrl: string;
    accountId: string;
  }>>> =>
    api.post('/stripe/connect'),

  // Get new onboarding link
  getOnboardingLink: (): Promise<AxiosResponse<ApiResponse<{
    onboardingUrl: string;
  }>>> =>
    api.get('/stripe/onboarding-link'),

  // Get account status
  getAccountStatus: (): Promise<AxiosResponse<ApiResponse<{
    connected: boolean;
    accountId?: string;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    onboardingCompleted: boolean;
    detailsSubmitted?: boolean;
    requirements?: any;
    cached?: boolean;
  }>>> =>
    api.get('/stripe/account-status'),

  // Get seller balance
  getBalance: (): Promise<AxiosResponse<ApiResponse<{
    available: { amount: number; currency: string }[];
    pending: { amount: number; currency: string }[];
  }>>> =>
    api.get('/stripe/balance'),

  // Force refresh status from Stripe
  refreshStatus: (): Promise<AxiosResponse<ApiResponse<{
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    onboardingCompleted: boolean;
  }>>> =>
    api.post('/stripe/refresh-status'),
};

// Chat API
export const chatAPI = {
  getConversations: () =>
    api.get('/chat'),

  getMessages: (offerId: string, params?: { limit?: number; before?: string }) =>
    api.get(`/chat/${offerId}`, { params }),

  sendMessage: (offerId: string, message: string) =>
    api.post(`/chat/${offerId}`, { message }),

  sendImage: (offerId: string, formData: FormData) =>
    api.post(`/chat/${offerId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getUnreadCount: (offerId: string) =>
    api.get(`/chat/${offerId}/unread`),
};

export const favoritesAPI = {
  add: (data: { matchId?: string; offerId?: string }) =>
    api.post('/favorites', data),

  list: (type?: 'match' | 'offer') =>
    api.get('/favorites', { params: type ? { type } : {} }),

  check: (params: { matchId?: string; offerId?: string }) =>
    api.get('/favorites/check', { params }),

  remove: (id: string) =>
    api.delete(`/favorites/${id}`),

  removeByTarget: (type: 'match' | 'offer', targetId: string) =>
    api.delete(`/favorites/by-target/${type}/${targetId}`),
};

export const searchesAPI = {
  save: (data: { name: string; filters: Record<string, any> }) =>
    api.post('/searches', data),

  list: () =>
    api.get('/searches'),

  remove: (id: string) =>
    api.delete(`/searches/${id}`),
};

export const recommendationsAPI = {
  get: (limit?: number) =>
    api.get('/recommendations', { params: { limit } }),

  similar: (matchId: string, limit?: number) =>
    api.get(`/recommendations/similar/${matchId}`, { params: { limit } }),
};

// Ratings API
export const ratingsAPI = {
  // Create a rating
  create: (data: { offerId: string; rating: number; comment?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/ratings', data),

  // Get seller ratings (public)
  getSellerRatings: (sellerId: string, params?: { page?: number; limit?: number }): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/ratings/seller/${sellerId}`, { params }),

  // Get rating for specific offer
  getOfferRating: (offerId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(`/ratings/offer/${offerId}`),

  // Get my ratings (given and received)
  getMyRatings: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/ratings/my-ratings'),

  // Update a rating
  update: (id: string, data: { rating?: number; comment?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/ratings/${id}`, data),
};

// Two-Factor Authentication API
export const twoFactorAPI = {
  setup: (): Promise<AxiosResponse<ApiResponse<{ qrCode: string; secret: string }>>> =>
    api.post('/auth/2fa/setup'),

  verify: (code: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/auth/2fa/verify', { code }),

  disable: (code: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/auth/2fa/disable', { code }),

  validate: (tempToken: string, code: string): Promise<AxiosResponse<ApiResponse<{ user: User; accessToken: string; refreshToken: string }>>> =>
    api.post('/auth/2fa/validate', { tempToken, code }),
};

export default api;